import React, { useState } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  TableBody,
  Table,
  TableRow,
  TableCell,
  Grid,
} from "@mui/material";
import { layoutCSS } from "../component/common/commonStyle";
import {
  useSubmitConnectionsMutation,
  useGetConnectionQuery,
} from "../store/services/createDbApi";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function FieldForm() {
  const [fieldName, setFieldName] = useState("");
  const [dataType, setDataType] = useState("");
  const [size, setSize] = useState("");
  const [submittedFields, setSubmittedFields] = useState([]);

  const { data: getconnection, refetch } = useGetConnectionQuery();
  const [submitConnections, { isLoading, isError, error }] = useSubmitConnectionsMutation();

  const maxFields = getconnection?.noofdatacolumns ?? 0;
  const connectionData = getconnection?.data || [];

  // How many more fields the user is allowed to add in total, and how many
  // of that allowance is still free after accounting for fields already
  // staged in submittedFields (but not yet sent to the API).
  const remainingFields = Math.max(maxFields - connectionData.length, 0);
  const remainingAfterStaged = Math.max(remainingFields - submittedFields.length, 0);
  const limitReached = remainingAfterStaged <= 0;

  const handleSubmit = () => {
    if (!fieldName || !dataType) {
      return; // Simply return without showing any message
    }

    // Hard stop: never let staged fields exceed the allowed limit.
    if (limitReached) {
      toast.error(`You can only add ${remainingFields} field(s).`);
      return;
    }

    if (dataType === "varchar" && Number(size) > 100) {
      toast.error("Max size is 100.");
      return;
    }

    const newField = {
      name: fieldName,
      type: dataType,
      size: dataType === "varchar" ? size || "" : "",
    };

    setSubmittedFields([...submittedFields, newField]);
    setFieldName("");
    setDataType("");
    setSize("");
  };

  const handleFinalSubmit = async () => {
    if (submittedFields.length === 0) {
      toast.error("Please add at least one field before submitting.");
      return;
    }

    if (submittedFields.length > remainingFields) {
      toast.error(`You can only submit up to ${remainingFields} field(s).`);
      return;
    }

    // SAFE JSON FORMAT
    const fields = submittedFields.map((field) => ({
      name: field.name
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_"), // convert spaces to underscore

      type:
        field.type === "varchar"
          ? `varchar(${field.size || 100})`
          : field.type,
    }));

    const data = { fields };

    console.log("Sending data to API:", data); // Debug log to see what's being sent

    try {
      // THIS IS WHERE THE API CALL HAPPENS
      const response = await submitConnections(data).unwrap();

      console.log("API Response:", response); // Debug log to see the response

      await refetch();

      // Show success message from backend
      if (response?.message) {
        toast.success(response.message);
      } else if (response?.data?.message) {
        toast.success(response.data.message);
      } else if (response?.success) {
        toast.success(response.success);
      } else if (typeof response === 'string') {
        toast.success(response);
      }

      setSubmittedFields([]);
    } catch (error) {
      console.error("API Error:", error); // Debug log for errors

      // Extract error message from backend response
      let backendErrorMessage = null;

      // Check different error response structures
      if (error?.data?.message) {
        backendErrorMessage = error.data.message;
      } else if (error?.data?.error) {
        if (typeof error.data.error === 'string') {
          backendErrorMessage = error.data.error;
        } else if (error.data.error?.message) {
          backendErrorMessage = error.data.error.message;
        } else if (Array.isArray(error.data.error)) {
          backendErrorMessage = error.data.error.join(', ');
        } else if (typeof error.data.error === 'object') {
          backendErrorMessage = JSON.stringify(error.data.error);
        }
      } else if (error?.data?.errors) {
        if (typeof error.data.errors === 'string') {
          backendErrorMessage = error.data.errors;
        } else if (Array.isArray(error.data.errors)) {
          backendErrorMessage = error.data.errors.join(', ');
        } else if (typeof error.data.errors === 'object') {
          // Handle field-specific errors
          const errorMessages = [];
          Object.keys(error.data.errors).forEach(key => {
            const value = error.data.errors[key];
            if (Array.isArray(value)) {
              errorMessages.push(...value);
            } else if (typeof value === 'string') {
              errorMessages.push(value);
            } else if (value?.message) {
              errorMessages.push(value.message);
            }
          });
          backendErrorMessage = errorMessages.join(', ');
        }
      } else if (error?.data?.detail) {
        backendErrorMessage = error.data.detail;
      } else if (error?.data) {
        if (typeof error.data === 'string') {
          backendErrorMessage = error.data;
        }
      }

      // Show backend error message if found
      if (backendErrorMessage) {
        toast.error(backendErrorMessage);
      }
    }
  };

  const inputSx = {
    minWidth: 140,
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.7)",
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#94A3B8" },
    },
    "& .MuiInputLabel-root": { color: "#475569" },
    "& .Mui-disabled": {
      "& .MuiOutlinedInput-input": {
        color: "#334155 !important",
        WebkitTextFillColor: "#334155 !important",
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
    },
  };

  return (
    <Box p={4} sx={layoutCSS}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Create Fields
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          elevation: 2,
          borderRadius: "0.5rem",
          padding: "1rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
          mb: 2,
        }}
      >
        <Table aria-label="field table">
          <TableBody>
            <TableRow>
              {connectionData.map((field, index) => (
                <TableCell
                  key={`conn-${index}`}
                  sx={{
                    backgroundColor: "#f5f4f4ff",
                    borderLeft: "5px solid #007bff",
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    whiteSpace: "nowrap",
                    textAlign: "left",
                    p: 2,
                  }}
                >
                  <Typography variant="body2">{field.column_name}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>

        <Typography
          variant="subtitle2"
          mt={2}
          color={limitReached ? "#d32f2f" : "#607d8b"}
          textAlign="right"
          fontStyle="italic"
        >
          {limitReached
            ? "Field limit reached"
            : `${remainingAfterStaged} field(s) remaining`}
        </Typography>

        <Grid container spacing={2} mt={1}>
          {submittedFields.map((field, index) => (
            <Grid item xs={12} sm={6} md={4} key={`submitted-${index}`}>
              <Box
                sx={{
                  backgroundColor: "rgba(186, 230, 253, 0.35)",
                  borderLeft: "5px solid #007bff",
                  borderRadius: 2,
                  p: 2,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                >
                  {field.name}
                </Typography>

                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <strong>Type:</strong> {field.type}
                </Typography>

                {field.size && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <strong>Size:</strong> {field.size}
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {remainingFields > 0 && !limitReached && (
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            label="Field Name"
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            size="small"
            sx={inputSx}
          />
          <TextField
            select
            label="Data Type"
            value={dataType}
            onChange={(e) => setDataType(e.target.value)}
            size="small"
            sx={inputSx}
          >
            <MenuItem value="varchar">String</MenuItem>
            <MenuItem value="numeric">Number</MenuItem>
            <MenuItem value="bit">Yes/No</MenuItem>
            <MenuItem value="date">Date</MenuItem>
          </TextField>
          {dataType === "varchar" && (
            <TextField
              label="Field Size"
              type="number"
              value={size}
              size="small"
              onChange={(e) => setSize(e.target.value)}
              onBlur={(e) => {
                if (e.target.value === "") return;
                if (Number(e.target.value) > 100) setSize("100");
                if (Number(e.target.value) < 1) setSize("1");
              }}
              error={Number(size) > 100}
              helperText={Number(size) > 100 ? "Max size is 100" : ""}
              inputProps={{ max: 100, min: 1 }}
              sx={inputSx}
            />
          )}
          <Button
            variant="outlined"
            onClick={handleSubmit}
            disabled={dataType === "varchar" && Number(size) > 100}
          >
            <PlaylistAddIcon /> Add Field
          </Button>
        </Box>
      )}

      {limitReached && (
        <Typography variant="body2" sx={{ color: "#d32f2f", mb: 2 }}>
          You've reached the maximum number of fields ({maxFields}). Remove a
          field below or submit to free up space.
        </Typography>
      )}

      <Button
        variant="contained"
        sx={{ backgroundColor: "#007bff", color: "white", mt: 2 }}
        onClick={handleFinalSubmit}
        disabled={isLoading || submittedFields.length === 0}
      >
        {isLoading ? "Submitting..." : "Submit"}
      </Button>
    </Box>
  );
}